/**
 * Parses the import-progress string the backend persists to `job3Import.notes`
 * so the UI can render a percentage / progress bar for a long-running import.
 *
 * The Importer's `ImportProgressTracker` writes notes in the exact shape:
 *   "Progress: {stage} - {pct}% complete. Processed: {n}/{total} resources. Status: {message}"
 * (see backend `ImportProgressTracker.UpdateProgressAsync`). Notes that do not
 * start with "Progress:" (e.g. reconciliation or error summaries) return null.
 */

export interface JobProgress {
    /** Pipeline stage label, e.g. "apply". */
    stage: string;
    /** Percent complete, clamped to 0–100. */
    percent: number;
    /** Resources processed so far, when present. */
    processed?: number;
    /** Total resources, when present. */
    total?: number;
    /** Human-readable status message, when present. */
    statusMessage?: string;
}

const PROGRESS_PREFIX = 'Progress:';

/**
 * Parses a `job3Import.notes` string into structured progress, or returns null
 * when the note is not a progress line or cannot be parsed.
 */
export function parseJobProgress(notes: string | null | undefined): JobProgress | null {
    if (!notes || !notes.startsWith(PROGRESS_PREFIX)) return null;

    // "Progress: {stage} - {pct}% complete. ..."
    const stageMatch = /^Progress:\s*(.*?)\s*-\s*([\d.]+)%\s*complete\.?/i.exec(notes);
    if (!stageMatch) return null;

    const stage = stageMatch[1]?.trim() ?? '';
    const rawPercent = Number.parseFloat(stageMatch[2] ?? '');
    if (Number.isNaN(rawPercent)) return null;
    const percent = Math.max(0, Math.min(100, rawPercent));

    const result: JobProgress = { stage, percent };

    const processedMatch = /Processed:\s*(\d+)\s*\/\s*(\d+)\s*resources/i.exec(notes);
    if (processedMatch) {
        result.processed = Number.parseInt(processedMatch[1] ?? '', 10);
        result.total = Number.parseInt(processedMatch[2] ?? '', 10);
    }

    const statusMatch = /Status:\s*(.+)$/i.exec(notes);
    if (statusMatch) {
        const message = statusMatch[1]?.trim();
        if (message) result.statusMessage = message;
    }

    return result;
}
